/*
 * Copyright (c) New Cloud Technologies, Ltd., 2013-2024
 *
 * You can not use the contents of the file in any way without New Cloud Technologies, Ltd. written permission.
 * To obtain such a permit, you should contact New Cloud Technologies, Ltd. at https://myoffice.ru/contacts/
 *
 */

import { DocumentChangedPayload, TypeInfo } from '../types';
import BaseAmrApi from '../BaseAmrApi';

import { EditCommands, EditEvents, EditParams, SaveDocumentParam } from './types';

export enum OPEN_MODE_FOR_EDIT {
    default = 'default',
    readonly = 'readonly',
    review = 'review',
}

export class AmrEditApi extends BaseAmrApi<EditCommands, EditEvents> {
    /**
     * @param editorFrame - элемент `<iframe>`, в который встраивается редактор
     * @param origin - window.location.origin
     */
    constructor(editorFrame: HTMLIFrameElement, origin: string) {
        super(editorFrame, origin, ['error', 'pageReloadRequested', 'changeDocument', 'documentSaved']);
    }

    /**
     * Подписка на обработчик изменений в документе
     * @param callback - Колбэк-функция, срабатывающая при изменении в документе
     * @returns Колбэк-функция для отписки от изменений
     */
    onChange(callback: (payload: DocumentChangedPayload) => void) {
        return this.subscribe('changeDocument', callback);
    }

    /**
     * Подписка на успешное сохранение документа
     * @param callback - Колбэк-функция, срабатывающая при сохранении документе
     * @returns Колбэк-функция для отписки от изменений
     */
    onDocumentSaved(callback: () => void) {
        return this.subscribe('documentSaved', callback);
    }

    /**
     * Открытие документа
     */
    openDocument(params: EditParams): Promise<TypeInfo> {
        const openDocumentParams = this.getOpenParameters(params);
        return this.open({
            mode: params.mode,
            ...openDocumentParams,
        });
    }

    /**
     * Сохраняет документ
     * @param format - Формат документа. У кождого формата есть фиксированное расширение, в которое происходит сохранение.
     * @returns Возвращает массив байт данных файла
     */
    saveDocument(format: SaveDocumentParam): Promise<ArrayBuffer> {
        if (!Object.keys(AmrEditApi.getOptions().SAVE_DOCUMENT_FORMATS).includes(format)) {
            return Promise.reject(`Unknown document type "${format}"`);
        }

        return this.request('saveDocument', format);
    }
}

export default AmrEditApi;
